import React from "react";
import { styled, Box, useTheme } from "@mui/material";
import { ScrollableWithGradients } from "@components/Gradient";
import { DropdownSectionData } from "./types";

export const SectionWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.down("sm")]: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.overline.fontSize,
  lineHeight: theme.typography.overline.lineHeight,
  fontWeight: 600,
  color: theme.palette.text.tertiary,
  padding: theme.spacing(2, 6),
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,

  [theme.breakpoints.up("sm")]: {
    paddingLeft: theme.spacing(4),
  },
}));

export const OptionItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "highlighted",
})<{ selected: boolean; highlighted: boolean }>(
  ({ theme, selected, highlighted }) => ({
    padding: theme.spacing(1.5, 4),
    cursor: "pointer",
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    backgroundColor: selected
      ? theme.palette.action.selected
      : highlighted
        ? theme.palette.action.hover
        : "transparent",
    color: theme.palette.text.primary,

    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  })
);

interface SectionProps {
  searchQuery: string;
  section: DropdownSectionData;
  hasMultipleSections: boolean;
  isLastSection: boolean;
  selectedValue: string | number;
  onSelect: (value: string | number) => void;
  highlightedIndex: number;
  startIndex: number;
}

export const Section = ({
  searchQuery,
  section,
  hasMultipleSections,
  isLastSection,
  selectedValue,
  onSelect,
  highlightedIndex,
  startIndex,
}: SectionProps) => {
  const theme = useTheme();
  return (
    <SectionWrapper>
      {hasMultipleSections && <SectionHeader>{section.label}</SectionHeader>}
      <ScrollableWithGradients
        recalculateDeps={[searchQuery]}
        sx={{
          overflowX: "hidden",
          maxHeight: 180,
          borderBottom: !isLastSection
            ? `1px solid ${theme.palette.border.primary}`
            : "none",
          [theme.breakpoints.down("sm")]: {
            flex: 1,
            minHeight: 0,
            maxHeight: "unset",
            marginLeft: theme.spacing(6),
            paddingRight: theme.spacing(6),
          },
        }}
      >
        {section.options.map((option, optionIndex) => (
          <OptionItem
            key={option.value}
            selected={option.value === selectedValue}
            highlighted={startIndex + optionIndex === highlightedIndex}
            onClick={() => onSelect(option.value)}
            data-option-index={startIndex + optionIndex}
            data-testid={`dropdown-section-${section.testId ?? section.label}-${optionIndex}`}
            role="option"
            aria-selected={option.value === selectedValue}
          >
            {option.label}
          </OptionItem>
        ))}
      </ScrollableWithGradients>
    </SectionWrapper>
  );
};
